import { ChevronRight, type LucideIcon } from "lucide-react";

import {
    Collapsible,
    CollapsibleContent,
    CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
    SidebarGroup,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarMenuSub,
    SidebarMenuSubButton,
    SidebarMenuSubItem,
} from "@/components/ui/sidebar";
import { useSidebar } from "@/components/ui/sidebar-context";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { hasAllRequiredRoles } from "@/auth/roles";
import { cn } from "@/lib/utils";
import { useEffect, useRef } from "react";

type NavSubItem = {
    title: string;
    url: string;
    requiredRoles?: string[];
    items?: NavSubItem[];
};

type NavItem = {
    title: string;
    url: string;
    icon?: LucideIcon;
    isActive?: boolean;
    requiredRoles?: string[];
    items?: NavSubItem[];
};

const filterByRoles = <T extends { requiredRoles?: string[] }>(
    items: T[],
    userRoles: string[],
): T[] =>
    items.filter(
        (item) =>
            !item.requiredRoles ||
            item.requiredRoles.length === 0 ||
            hasAllRequiredRoles(userRoles, item.requiredRoles),
    );

const NavSubLeaf = ({
    item,
    pathname,
    wrap = false,
}: {
    item: NavSubItem;
    pathname: string;
    wrap?: boolean;
}) => (
    <SidebarMenuSubItem>
        <SidebarMenuSubButton
            asChild
            className={cn(
                wrap &&
                    "h-auto min-h-7 [&>span:last-child]:whitespace-normal [&>span:last-child]:break-words [&>span:last-child]:leading-tight",
            )}
        >
            <Link
                className={cn(item.url === pathname ? "bg-slate-200" : "")}
                to={item.url}
            >
                <span>{item.title}</span>
            </Link>
        </SidebarMenuSubButton>
    </SidebarMenuSubItem>
);

const NavSubGroup = ({
    item,
    pathname,
    userRoles,
}: {
    item: NavSubItem;
    pathname: string;
    userRoles: string[];
}) => {
    const children = filterByRoles(item.items ?? [], userRoles);
    if (children.length === 0) {
        return null;
    }

    const containsActive = children.some((c) => c.url === pathname);

    return (
        <Collapsible
            asChild
            className="group/sub-collapsible"
            defaultOpen={containsActive}
        >
            <SidebarMenuSubItem>
                <CollapsibleTrigger asChild>
                    <SidebarMenuSubButton type="button">
                        <span>{item.title}</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/sub-collapsible:rotate-90" />
                    </SidebarMenuSubButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                    <SidebarMenuSub className="mx-0 px-2">
                        {children.map((child) => (
                            <NavSubLeaf
                                item={child}
                                key={child.title}
                                pathname={pathname}
                                wrap
                            />
                        ))}
                    </SidebarMenuSub>
                </CollapsibleContent>
            </SidebarMenuSubItem>
        </Collapsible>
    );
};

export const NavMain = ({ items }: { items: NavItem[] }) => {
    const { userInfo } = useAuth();
    const { setOpenMobile, isMobile, setOpen } = useSidebar();
    const { pathname } = useLocation();
    const prevPathnameRef = useRef(pathname);
    const userRoles = userInfo?.roles ?? [];

    useEffect(() => {
        if (isMobile && prevPathnameRef.current !== pathname) {
            setOpenMobile(false);
            prevPathnameRef.current = pathname;
        }
    }, [pathname, isMobile, setOpenMobile]);

    const filteredItems = filterByRoles(items, userRoles);

    return (
        <SidebarGroup>
            <SidebarMenu>
                {filteredItems.map((item) =>
                    item.items && item.items.length > 0 ? (
                        <Collapsible
                            asChild
                            className="group/collapsible"
                            defaultOpen={item.isActive}
                            key={item.title}
                        >
                            <SidebarMenuItem>
                                <CollapsibleTrigger asChild>
                                    <SidebarMenuButton
                                        onClick={() => setOpen(true)}
                                        tooltip={item.title}
                                        type="button"
                                    >
                                        {item.icon ? <item.icon /> : null}
                                        <span>{item.title}</span>
                                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                                    </SidebarMenuButton>
                                </CollapsibleTrigger>
                                <CollapsibleContent>
                                    <SidebarMenuSub>
                                        {filterByRoles(
                                            item.items,
                                            userRoles,
                                        ).map((subItem) =>
                                            subItem.items &&
                                            subItem.items.length > 0 ? (
                                                <NavSubGroup
                                                    item={subItem}
                                                    key={subItem.title}
                                                    pathname={pathname}
                                                    userRoles={userRoles}
                                                />
                                            ) : (
                                                <NavSubLeaf
                                                    item={subItem}
                                                    key={subItem.title}
                                                    pathname={pathname}
                                                />
                                            ),
                                        )}
                                    </SidebarMenuSub>
                                </CollapsibleContent>
                            </SidebarMenuItem>
                        </Collapsible>
                    ) : (
                        <SidebarMenuItem key={item.title}>
                            <SidebarMenuButton asChild tooltip={item.title}>
                                <Link
                                    className={cn(
                                        item.url === pathname
                                            ? "bg-slate-200"
                                            : "",
                                    )}
                                    to={item.url}
                                >
                                    {item.icon ? <item.icon /> : null}
                                    <span>{item.title}</span>
                                </Link>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    ),
                )}
            </SidebarMenu>
        </SidebarGroup>
    );
};
